const GTM_SCRIPT_ATTR = "data-gtm-loader";

/** Resolves after `gtm.js` has loaded (or immediately if GTM is disabled / already handled). */
let gtmReadyPromise: Promise<void> | null = null;

declare global {
  interface Window {
    dataLayer?: unknown[];
    /** Set by `gtm.js` after the container bootstrap runs. */
    google_tag_manager?: Record<string, unknown>;
  }
}

/**
 * Resolves when the GTM bootstrap script has finished loading, so `dataLayer` pushes run after the
 * container can process them. If GTM was not configured, resolves immediately.
 */
export function whenGtmReady(): Promise<void> {
  return gtmReadyPromise ?? Promise.resolve();
}

/**
 * Legacy-style helper: pushes arguments to dataLayer (parity with old HTML login).
 */
function installGtagDataLayerShim(): void {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  if ("gtag" in window && typeof (window as unknown as { gtag?: unknown }).gtag === "function") {
    return;
  }
  (window as unknown as { gtag: (...args: unknown[]) => void }).gtag = function gtag(
    ...args: unknown[]
  ) {
    window.dataLayer!.push(...args);
  };
}

/**
 * Resolves after the GTM `gtm.js` script element has loaded (or failed). If `load` already fired
 * before we attached listeners, we detect readiness on the next microtask via `google_tag_manager`.
 */
function waitForGtmScriptElement(script: HTMLScriptElement): Promise<void> {
  return new Promise((resolve) => {
    const controller = new AbortController();
    const { signal } = controller;
    const done = (): void => {
      controller.abort();
      resolve();
    };
    script.addEventListener("load", done, { once: true, signal });
    script.addEventListener("error", done, { once: true, signal });

    queueMicrotask(() => {
      if (typeof window !== "undefined" && window.google_tag_manager) {
        done();
      }
    });
  });
}

/**
 * Loads Google Tag Manager once. Safe under React Strict Mode (guards duplicate script tags).
 */
export function initGtm(containerId: string | undefined): void {
  if (typeof document === "undefined") return;

  const id = containerId?.trim();
  if (!id) {
    gtmReadyPromise ??= Promise.resolve();
    return;
  }

  const existing = document.querySelector<HTMLScriptElement>(`script[${GTM_SCRIPT_ATTR}]`);
  if (existing) {
    gtmReadyPromise ??= waitForGtmScriptElement(existing);
    return;
  }

  window.dataLayer = window.dataLayer || [];
  installGtagDataLayerShim();

  window.dataLayer.push({
    "gtm.start": Date.now(),
    event: "gtm.js",
  });

  const firstScript = document.getElementsByTagName("script")[0];
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(id)}`;
  script.setAttribute(GTM_SCRIPT_ATTR, "true");

  gtmReadyPromise = waitForGtmScriptElement(script);

  const parent = firstScript?.parentNode;
  if (parent && firstScript) {
    parent.insertBefore(script, firstScript);
  } else {
    (document.head ?? document.documentElement).appendChild(script);
  }
}
